import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import SignaturePad from '@/components/reports/SignaturePad';

const SCRIPT_FONT = "'Brush Script MT', 'Segoe Script', 'Snell Roundhand', cursive";

/** Standard consent wording for client-facing report signatures. */
export const REPORT_SIGNATURE_CONSENT = 'By signing, you agree this electronic signature is a legally binding acknowledgement of this report.';

/**
 * Render a TYPED name in a cursive script onto an offscreen canvas and
 * return a PNG data URL — so a typed signature ships the SAME
 * `{ data_url, signature_type }` shape as a drawn one. Returns '' if the
 * canvas 2D context is unavailable.
 */
export function typedSignatureDataUrl(name) {
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#0a0a0c';
  ctx.textBaseline = 'middle';
  ctx.font = `italic 56px ${SCRIPT_FONT}`;
  ctx.fillText(name, 24, canvas.height / 2);
  return canvas.toDataURL('image/png');
}

/**
 * Controlled capture core shared by the public signing page and the
 * in-person report form: name, optional email, Draw/Type tabs, consent.
 * `prefix` namespaces ids + testids; `nameTestId` / `padTestId` override
 * those two. `padKey` remounts the pad so it re-seeds an outside value.
 */
export function SignatureCaptureFields({
  prefix = 'sig-capture', nameTestId, padTestId, padKey,
  name, onName, email, onEmail, showEmail = true,
  tab, onTab, drawn, onDraw, consentText, disabled = false,
}) {
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={`${prefix}-name`}>
          Full name <span className="text-red-500">*</span>
        </Label>
        <Input
          id={`${prefix}-name`}
          data-testid={nameTestId || `${prefix}-name`}
          value={name}
          disabled={disabled}
          onChange={(e) => onName(e.target.value)}
          placeholder="e.g. Jane Homeowner"
        />
      </div>

      {showEmail && <div className="space-y-1.5">
        <Label htmlFor={`${prefix}-email`}>
          Email <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <Input
          id={`${prefix}-email`}
          data-testid={`${prefix}-email`}
          type="email"
          value={email}
          disabled={disabled}
          onChange={(e) => onEmail(e.target.value)}
          placeholder="name@email.com"
        />
      </div>}

      <Tabs value={tab} onValueChange={onTab}>
        {!disabled && (
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="drawn" data-testid={`${prefix}-tab-drawn`}>Draw</TabsTrigger>
            <TabsTrigger value="typed" data-testid={`${prefix}-tab-typed`}>Type</TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="drawn" className="mt-3">
          <SignaturePad key={padKey} testId={padTestId || `${prefix}-pad`} value={drawn} onChange={onDraw} disabled={disabled} />
        </TabsContent>
        <TabsContent value="typed" className="mt-3">
          <div className="rounded-md border border-border bg-white px-4 py-6 min-h-[140px] flex items-center overflow-hidden">
            <span
              data-testid={`${prefix}-typed-preview`}
              className="truncate"
              style={{ fontFamily: SCRIPT_FONT, fontSize: 40, fontStyle: 'italic', color: '#0a0a0c' }}
            >
              {name.trim() || 'Your name'}
            </span>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Your typed name becomes your signature.</p>
        </TabsContent>
      </Tabs>

      {consentText ? (
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid={`${prefix}-consent`}>
          {consentText}
        </p>
      ) : null}
    </>
  );
}

/**
 * Surface-agnostic signature capture: props in, payload out, NO fetching
 * inside. On submit it always emits one shape:
 * `{ name, email?, data_url, signature_type: 'drawn'|'typed' }`.
 * `consentText` renders under the pad only when the caller passes it.
 * `blocked` keeps submit disabled for a caller-side gate (e.g. unchecked
 * acknowledgements); `showEmail={false}` hides the optional email input.
 * State lives here, so a failed submit keeps the name + drawing intact.
 */
export function SignatureCaptureForm({
  onSubmit, submitting = false, submitLabel = 'Submit signature', consentText,
  initialName = '', showEmail = true, blocked = false,
}) {
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState('');
  const [tab, setTab] = useState('drawn');
  const [drawn, setDrawn] = useState('');
  const [error, setError] = useState('');

  const nameOk = name.trim().length > 0;
  const canSubmit = nameOk && (tab === 'typed' || !!drawn) && !submitting && !blocked;

  const handleSubmit = () => {
    if (!nameOk) { setError('Please enter your name.'); return; }
    let dataUrl = '';
    if (tab === 'drawn') {
      if (!drawn) { setError('Please draw your signature.'); return; }
      dataUrl = drawn;
    } else {
      dataUrl = typedSignatureDataUrl(name.trim());
    }
    setError('');
    onSubmit?.({
      name: name.trim(),
      email: email.trim() || undefined,
      data_url: dataUrl,
      signature_type: tab,
    });
  };

  return (
    <div className="space-y-4" data-testid="sig-capture">
      <SignatureCaptureFields
        name={name} onName={setName} email={email} onEmail={setEmail} showEmail={showEmail}
        tab={tab} onTab={setTab} drawn={drawn} onDraw={setDrawn}
      />

      {error ? (
        <p className="text-sm text-red-500" data-testid="sig-capture-error">{error}</p>
      ) : null}

      {consentText ? (
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="sig-capture-consent">
          {consentText}
        </p>
      ) : null}

      <Button
        data-testid="sig-capture-submit"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full bg-[#4575A8] hover:bg-[#5C8AC2] text-white"
      >
        {submitting ? 'Submitting…' : submitLabel}
      </Button>
    </div>
  );
}

export default SignatureCaptureForm;
